<script>
  import Highlight from "svelte-highlight";
  import swift from "svelte-highlight/src/languages/swift"
  import xcode from "svelte-highlight/src/styles/xcode";
  import {push} from "../EndpointsAndTransforms";
  import {createEventDispatcher} from "svelte";

  let editor

  const dispatch = createEventDispatcher()
  function goBack() {
    dispatch('back')
  }

  const base64Conversion =
    '///Translate the URL base64 encoded strings from the web into the kind of base64 Foundation.framework wants\n' +
    '///\n' +
    '/// Swaps out \'dash\' and \'underscore\' for \'+\', \'/\', and padds the end with \'=\' so the length % 4 = 0\n' +
    'fileprivate func base64(_ from: Base64URLEncodedString) -> Base64EncodedString {\n' +
    '    var transformed = from\n' +
    '        .replacingOccurrences(of: "-", with: "+")\n' +
    '        .replacingOccurrences(of: "_", with: "/")\n' +
    '    let modulus = transformed.count % 4\n' +
    '    if modulus != 0 {\n' +
    '        transformed += String(repeating: "=", count: 4 - modulus)\n' +
    '    }\n' +
    '    return transformed\n' +
    '}'

  const publicKeyEncode = 'fileprivate let secKey: SecKey? = {\n' +
    '    let publicKeyString = "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA1bhcT24GXjU6IAD9oYL6RGHeDOiY74Iyfa1jw4BjY3RhRpAaAV+YN5lwwYGW3AeD7iMx1qwvYY0AaiXC9VVhiXU2P/4ntIay9rM0CUJFMcT6ExLWOLER1G8iFbqAm77bjy5GzA0IHqM0AQcUUXCNmtHCiHIavT+d7IW+FsvnrY6fB7/jeGb1FWFA71/eJ2pPfS9quKluOzxXYgcuAft7x9F9mlEJaK5M0tCc50RgZPYNzDe+vvD38ptWAIBJ5bAJh66mEYPvYUNgoh0tMZxdDVCeY7WreEcmOGiYxcKYnGmqsadX6X+N6qlXvrT6t/ypx4WPl1kUjU/Qg57buwBCfwIDAQAB"\n' +
    '    let attributes: [String:Any] =\n' +
    '    [\n' +
    '        kSecAttrKeyClass as String: kSecAttrKeyClassPublic,\n' +
    '        kSecAttrKeyType as String: kSecAttrKeyTypeRSA,\n' +
    '        kSecAttrKeySizeInBits as String: 2048,\n' +
    '    ]\n' +
    '    \n' +
    '    guard let secKeyData = Data.init(base64Encoded: publicKeyString) else { return nil }\n' +
    '    guard let key = SecKeyCreateWithData(secKeyData as CFData, attributes as CFDictionary, nil) else { return nil }\n' +
    '    return key\n' +
    '}()'

  const verify = '///Uses Security.framework\'s RSA signature verification implementation to compare the JWT signature of each header.body for idToken and accessToken.\n' +
    '///\n' +
    '///Currently assumes the `.rsaSignatureMessagePKCS1v15SHA256` scheme.\n' +
    'fileprivate func verify(header: Base64URLEncodedString, body: Base64URLEncodedString, signature: Base64EncodedString, key: SecKey) -> Bool {\n' +
    '    guard let payloadData = "\\(header).\\(body)".data(using: .ascii) else { return false }\n' +
    '    guard let signatureData = Data(base64Encoded: signature) else { return false }\n' +
    '    return SecKeyVerifySignature(key, .rsaSignatureMessagePKCS1v15SHA256, payloadData as CFData, signatureData as CFData, nil)\n' +
    '}'

</script>

<svelte:head>
  {@html xcode}
</svelte:head>

<main>
  <goBackArea on:click="{() => {
    push('rants')
    goBack()
  }}">
    <p><i>back to list</i></p>
  </goBackArea>

  <h2>Cryptography.jsonRepresentation()</h2>
  <p>In the course of learning about AWS Cognito and how it could play with my Swift Lambda
    functions, I was introduced to something which I'm sure most web-devs consider to be old
    hat, but was new to me: JSON Web Tokens</p>
  <p>Ever since the undergraduate Number Theory course I took in math school, I've always been
    easily distracted by all things cryptography adjacent.</p>
  <p>I hadn't even read that much about it yet, but shortly after Cognito started spraying
    intriguing piles of base64 encoded gibberish at my Lambda's, I knew it was
    <code>File >> New</code> time on a Swift playground...</p>

  <h5>Verify & Parse</h5>
  <p>The basic shape of the work to be done is this:</p>
  <ul>
    <p>1. User authenticates with Cognito</p>
    <p>2. Cognito bundles up some info about the user and the current time, uses your private key
      to sign it with SHA256 and hands it back to your app</p>
    <p>3. If you're so inclined, you can use the public key to verify the signature, and parse out
      the info that was bundled by Cognito (things like username and email address).</p>
    <p>4. Then you can condition/parameterize your Lambda's work based on the verification &
      info</p>
  </ul>

  <p><img src="/images/JWTexample.png"/>The tokens look like this. A bunch of base64 encoded
    strings, separated by <code>'.'</code>.
  </p>

  <p>The first challenge we come across is that <code>Base64URLEncodedString</code> encoding is
    different than <code>Foundation.framework Base64EncodedString</code> </p>
  <p>You may need to pad the length out to be cleanly divisible by 4, and you'll have to swap a
    couple of separator characters</p>
  <p>This does the trick:</p>
  <codeContainer>
    <Highlight language={swift} code={base64Conversion}/>
  </codeContainer>

  <p>After that, some string manipulation grind separates out the different components you'll
    need later from the token. Not very interesting code, but you can see it in the gist.</p>
  <p>If we're using Apple's <code>Security.framework</code> for our cryptographic needs, then we
  can handle our next task (turning the public side of our Cognito signing key into something
    useful) like this:</p>
  <codeContainer>
    <Highlight language={swift} code={publicKeyEncode}/>
  </codeContainer>

  <p>And the final step is to trust, but <code>SecKeyVerifySignature</code></p>
  <codeContainer>
    <Highlight language={swift} code={verify}/>
  </codeContainer>

  <h5>So can it live in a Lambda function?</h5>

  <p>Had I not been so excited about an excuse to do amatuer cryptography, I might have noticed
    that <code>Security.framework</code> is written in Objective-C...</p>
  <p>Obviously that's not a problem when I'm running my Lambda function locally on my *checks
    notes* Mac OSX laptop... Everything seemed to work just fine, and my locally running Lambda
    was happily chewing through the tokens, verifying & parsing them with ease.</p>
  <p>Fortunately when `swiftc` went to cross-compile with my AmazonLinux2 toolchain, my mistake
    was instantly identified. Pure Swift dependencies only, Ryan! C'mon!!</p>

  <h5>Epilogue</h5>
  <p>So if I really wanted to handle JWT's with my Swift Lambda's, I'd need to get a pure Swift
    crypto primatives alternative working.</p>
  <p>The very excellent <a href="https://github.com/krzyzanowskim/CryptoSwift">Crypto-Swift</a>
    project does have SHA256 tools, and could probably do the job if needed.
  </p>

  <p><img src="/images/APIGatewayJWT.png"/>But luckily I did a tiny bit more reading first, and
    realized that APIGateway lets you hook up Cognito Auth to your routes with a dropdown menu..
    .</p>
  <p>There's a little more configuration to it than just that one drop-down, but within 5
    minutes, I was good to go.</p>
  <p>I promptly discarded my changes and deleted my branch...</p>
  <p>But I did at least save the Swift.playground, which I've
    <a href="https://gist.github.com/ryancumley/f59f1672e3b6345c68b1ed129073d46f">put up as a
      gist for you here</a></p>

</main>


<style>
  main {
    display: flex;
    flex-direction: column;
    flex-width: nowrap;
    justify-content: flex-start;
    overflow-y: scroll;
    scrollbar-width:none;

    text-align: left;
    max-width: 800px;
    margin-right: auto; margin-left: auto;
    padding-left: 1rem; padding-right: 1rem;
  }
  main::-webkit-scrollbar {
    display: none;
  }
  codeContainer {
    max-width: 95%;
    margin: 1rem;
    border: solid 1px black;
    font-size: 1.5rem;
  }
  h5 {
    margin-bottom: 0px;
  }
  h6 {
    margin-bottom: 0px; margin-top: 10px;
  }
  p {
    text-align: left;
    max-width: 42rem;
    padding: 1rem;
    /*margin: 0 0 0 1rem;*/
    margin: 0;
    line-height: 2rem;
    font-size: 22px;
    font-weight: 400;
  }
  strong {
    font-weight: 700;
  }
  ul p {
    padding: 3px;
  }
  img {
    max-width: 350px;
    height: auto;
    float: right;
    margin: 4px;
    padding-left: 10px; padding-right: 10px;
    -webkit-filter: drop-shadow(5px 7px 5px #272d35);
    filter: drop-shadow(5px 7px 5px #272d35);
  }
  leftFloat img {
    float: left;
  }

  fullWidth img {
    max-width: 97%;
    height: auto;
    padding-left: 10px; padding-right: 10px;
    -webkit-filter: drop-shadow(5px 7px 5px #272d35);
    filter: drop-shadow(5px 7px 5px #272d35);
  }

  goBackArea {
    max-width: 10rem;
    margin-top: 1rem;
  }
  goBackArea:hover {
    box-shadow: -2px 9px 10px -4px #272d35, 0px -9px 10px -4px #272d35;
    border-radius: 8px;
    background-color: rgb(248,248,248);
  }

  code {
    background-color: whitesmoke;
  }

  @media only screen and (max-width: 600px) {
    h2 {
      font-size: 2.0rem;
    }
    p {
      max-width: 95%;
    }
    fullWidth img {
      max-width: 92%;
    }
    main {
      overflow-x: hidden;
      max-width: 97vw;
    }

  }
</style>
